import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Globe, Images, Loader2, Save, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail, resolveImage } from "@/lib/api";
import MediaLibraryPicker from "@/components/MediaLibraryPicker";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Counter({ value, max }) {
  const len = (value || "").length;
  const over = len > max;
  return <span className={`text-[11px] ${over ? "text-terra" : "text-stone-mute"}`}>{len}/{max}</span>;
}

function PageEditor({ page, onSaved }) {
  const [form, setForm] = useState({ ...page });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/seo/page/${page.page_key}`, {
        title: form.title, description: form.description, keywords: form.keywords, image_url: form.image_url,
      });
      toast.success(`SEO « ${page.label} » enregistré`);
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/seo/generate", { page_key: page.page_key });
      setForm((f) => ({ ...f, title: data.title || f.title, description: data.description || f.description, keywords: data.keywords || f.keywords }));
      toast.success("Proposition IA insérée — ajustez puis enregistrez");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Génération impossible");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid={`seo-page-${page.page_key}`} className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-serif text-xl font-semibold text-bark">{page.label}</h3>
          <span className="text-xs text-stone-mute">{page.path}</span>
        </div>
        <button
          data-testid={`seo-generate-${page.page_key}`}
          onClick={generate}
          disabled={generating}
          className="flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-4 py-2 text-xs font-semibold text-bark transition-colors hover:border-amber-deep hover:text-amber-deep disabled:opacity-60"
        >
          {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Générer avec l'IA
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-sm font-medium text-bark">Titre (balise title)</label>
            <Counter value={form.title} max={60} />
          </div>
          <input data-testid={`seo-title-${page.page_key}`} value={form.title || ""} onChange={upd("title")}
            className="w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-2.5 text-sm text-bark outline-none focus:border-amber-deep" />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-sm font-medium text-bark">Meta description</label>
            <Counter value={form.description} max={160} />
          </div>
          <textarea data-testid={`seo-desc-${page.page_key}`} value={form.description || ""} onChange={upd("description")} rows={3}
            className="w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-2.5 text-sm text-bark outline-none focus:border-amber-deep" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-bark">Mots-clés (séparés par des virgules)</label>
          <input data-testid={`seo-keywords-${page.page_key}`} value={form.keywords || ""} onChange={upd("keywords")}
            className="w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-2.5 text-sm text-bark outline-none focus:border-amber-deep" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-bark">Image de partage (URL) — facultatif</label>
          <div className="flex gap-2">
            <input data-testid={`seo-image-${page.page_key}`} value={form.image_url || ""} onChange={upd("image_url")} placeholder="Laisser vide pour utiliser l'image par défaut"
              className="w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-2.5 text-sm text-bark outline-none focus:border-amber-deep" />
            <button type="button" data-testid={`seo-image-library-${page.page_key}`} onClick={() => setPickerOpen(true)}
              className="flex shrink-0 items-center gap-1.5 rounded-xl border border-amber-900/20 px-3 text-sm font-medium text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep">
              <Images size={15} />
            </button>
          </div>
        </div>
        <button data-testid={`seo-save-${page.page_key}`} onClick={save} disabled={saving}
          className="flex items-center gap-2 rounded-full bg-amber-deep px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-honey disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
        </button>
      </div>
      <MediaLibraryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => setForm((f) => ({ ...f, image_url: resolveImage(url) }))}
        defaultTag="bannieres"
      />
    </div>
  );
}

const GLOBAL_FIELDS = [
  { k: "site_name", label: "Nom du site" },
  { k: "meta_description", label: "Description par défaut" },
  { k: "default_image_url", label: "Image de partage par défaut (URL)" },
  { k: "facebook_url", label: "Page Facebook (URL)" },
  { k: "instagram_url", label: "Compte Instagram (URL)" },
  { k: "youtube_url", label: "Chaîne YouTube (URL)" },
  { k: "telephone", label: "Téléphone" },
  { k: "street_address", label: "Adresse" },
  { k: "postal_code", label: "Code postal" },
  { k: "city", label: "Ville" },
  { k: "region", label: "Région" },
];

function GlobalEditor({ global, onSaved }) {
  const [form, setForm] = useState({ ...global });
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/seo/global", form);
      toast.success("Réglages globaux enregistrés");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="seo-global" className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Globe size={18} className="text-amber-deep" />
        <h2 className="font-serif text-xl font-semibold text-bark">Réglages globaux</h2>
      </div>
      <p className="mb-4 text-sm text-bark-soft">
        Nom du site, image de partage par défaut, réseaux sociaux et coordonnées (utilisés pour Open Graph et les données structurées « miellerie locale »).
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        {GLOBAL_FIELDS.map((f) => (
          <div key={f.k} className={f.k === "meta_description" ? "sm:col-span-2" : ""}>
            <label className="mb-1 block text-sm font-medium text-bark">{f.label}</label>
            {f.k === "default_image_url" ? (
              <div className="flex gap-2">
                <input data-testid={`seo-global-${f.k}`} value={form[f.k] || ""} onChange={upd(f.k)}
                  className="w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-2.5 text-sm text-bark outline-none focus:border-amber-deep" />
                <button type="button" data-testid="seo-global-image-library" onClick={() => setPickerOpen(true)}
                  className="flex shrink-0 items-center gap-1.5 rounded-xl border border-amber-900/20 px-3 text-sm font-medium text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep">
                  <Images size={15} />
                </button>
              </div>
            ) : (
              <input data-testid={`seo-global-${f.k}`} value={form[f.k] || ""} onChange={upd(f.k)}
                className="w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-2.5 text-sm text-bark outline-none focus:border-amber-deep" />
            )}
          </div>
        ))}
      </div>
      <MediaLibraryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => setForm((f) => ({ ...f, default_image_url: resolveImage(url) }))}
        defaultTag="bannieres"
      />
      <button data-testid="seo-global-save" onClick={save} disabled={saving}
        className="mt-5 flex items-center gap-2 rounded-full bg-amber-deep px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-honey disabled:opacity-60">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
      </button>
    </div>
  );
}

export default function AdminSeo() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/seo").then(({ data }) => setData(data));
  }, []);

  if (!data) {
    return (
      <div className="flex items-center justify-center gap-3 py-32 text-bark-soft">
        <Loader2 className="animate-spin text-amber-deep" size={26} />
        <span className="font-serif text-lg">Chargement…</span>
      </div>
    );
  }

  return (
    <div data-testid="admin-seo-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-bark">Référencement (SEO)</h1>
          <p className="mt-1 text-sm text-bark-soft">
            Optimisez le titre, la description et le partage de chaque page. Les fiches produits et articles sont référencées automatiquement.
          </p>
        </div>
        <div className="flex gap-2">
          <a data-testid="seo-sitemap-link" href={`${API_BASE}/sitemap.xml`} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 rounded-full border border-amber-800/25 bg-white px-4 py-2 text-xs font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep">
            <ExternalLink size={14} /> sitemap.xml
          </a>
          <a data-testid="seo-robots-link" href={`${API_BASE}/robots.txt`} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 rounded-full border border-amber-800/25 bg-white px-4 py-2 text-xs font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep">
            <ExternalLink size={14} /> robots.txt
          </a>
        </div>
      </div>

      <GlobalEditor global={data.global} onSaved={(g) => setData((d) => ({ ...d, global: g }))} />

      <h2 className="pt-2 font-serif text-2xl font-semibold text-bark">Pages du site</h2>
      {data.pages.map((p) => (
        <PageEditor key={p.page_key} page={p}
          onSaved={(updated) => setData((d) => ({ ...d, pages: d.pages.map((x) => (x.page_key === updated.page_key ? updated : x)) }))} />
      ))}
    </div>
  );
}
